import React, { useRef, useEffect, CSSProperties } from 'react';
import { Label } from '../ui/label';
import { RefreshCcw } from 'lucide-react';

interface RangeInputProps {
    min: number;
    max: number;
    value: number;
    onChange: (value: number) => void;
    color?: string;
    trackColor?: string;
    thumbColor?: string;
    label?: string;
    showValue?: boolean;
    step?: number;
    unit?: string;
    disabled?: boolean;
    className?: string;
    classNameLabel?: string;
    oldValue?: number;
}

export const RangeInputComponent = React.forwardRef<HTMLInputElement, RangeInputProps>(
    (
        {
            min,
            max,
            value,
            onChange,
            color = '#3b82f6',
            trackColor = '#e5e7eb',
            thumbColor = '#ffffff',
            label,
            showValue = true,
            step = 1,
            unit = '',
            disabled = false,
            className = '',
            classNameLabel = '',
            oldValue
        },
        ref
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const progressRef = useRef<HTMLDivElement>(null);
        const trackRef = useRef<HTMLDivElement>(null);

        // Sync ref
        useEffect(() => {
            if (typeof ref === 'function') {
                ref(inputRef.current);
            } else if (ref) {
                ref.current = inputRef.current;
            }
        }, [ref]);

        // Update progress bar width
        useEffect(() => {
            if (progressRef.current && inputRef.current) {
                const percentage = ((value - min) / (max - min)) * 100;
                progressRef.current.style.width = `${percentage}%`;
            }
        }, [value, min, max]);

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange(Number.parseFloat(e.target.value));
        };

        const percentage = ((value - min) / (max - min)) * 100;

        const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if (disabled || !trackRef.current) return;

            const rect = trackRef.current.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const percentage = clickX / rect.width;
            const newValue = min + percentage * (max - min);

            // Round to nearest step
            const steppedValue = Math.round(newValue / step) * step;
            const clampedValue = Math.max(min, Math.min(max, steppedValue));

            onChange(clampedValue);
        };

        return (
            <div className={`w-full ${className}`}>
                <div className="flex justify-between mb-2">
                    <div className='flex gap-2 items-center'>
                        {label && (
                            <Label
                                htmlFor={label as string}
                                className={`${classNameLabel || 'text-sm font-medium text-gray-700'}`}
                            >
                                {label}
                            </Label>
                        )}
                        {oldValue !== undefined && oldValue !== null && oldValue !== value && (
                            <RefreshCcw
                                onClick={() => {
                                    onChange(oldValue);
                                }}
                                className="cursor-pointer transition-all w-4 h-4 hover:rotate-45 text-primary"
                            />
                        )}
                    </div>
                    {showValue && (
                        <span className="text-sm font-semibold text-foreground">
                            {value}
                            {unit}
                        </span>
                    )}
                </div>

                <div
                    ref={trackRef}
                    onClick={handleTrackClick}
                    className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer"
                >
                    {/* Progress bar */}
                    <div
                        ref={progressRef}
                        className="absolute h-full rounded-full transition-all"
                        style={{ backgroundColor: color, width: `${percentage}%` }}
                    />

                    {/* Hidden input */}
                    <input
                        ref={inputRef}
                        type="range"
                        min={min}
                        max={max}
                        value={value}
                        step={step}
                        onChange={handleChange}
                        disabled={disabled}
                        className="absolute w-full h-full top-0 left-0 appearance-none bg-transparent cursor-pointer pointer-events-none opacity-0 z-10 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:cursor-pointer"
                        style={
                            {
                                '--thumb-color': thumbColor,
                                '--track-color': trackColor,
                            } as CSSProperties
                        }
                    />

                    {/* Visible thumb - positioned by JavaScript */}
                    <div
                        className="absolute w-5 h-5 rounded-full shadow-md top-1/2 -translate-y-1/2 pointer-events-none transition-all"
                        style={{
                            left: `calc(${percentage}% - 10px)`,
                            backgroundColor: thumbColor,
                            boxShadow: `0 0 0 3px ${color}40`,
                        }}
                    />
                </div>

                {!label && showValue && (
                    <div className="mt-2 text-right text-sm font-semibold text-foreground">
                        {value}
                        {unit}
                    </div>
                )}
            </div>
        );
    }
);

RangeInputComponent.displayName = 'RangeInputComponent';
